import type { Appearance } from "@stripe/stripe-js"

/**
 * A theme token from index.css ("240 4% 16%") as hex, or rgba() with an alpha.
 * Stripe's variables take hex only: it silently drops hsla() and rgba() there.
 */
const token = (name: string, alpha = 1): string => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--${name}`)
    .trim()
  if (!value) return ""
  const [h, s, l] = value.split(/\s+/).map((part) => parseFloat(part))
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100)
  const [r, g, b] = [0, 8, 4].map((n) => {
    const k = (n + h / 30) % 12
    return Math.round(
      255 * (l / 100 - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))),
    )
  })
  if (alpha < 1) return `rgba(${r}, ${g}, ${b}, ${alpha})`
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`
}

/**
 * Stripe's fields in the look of our Input (components/ui/input.tsx). The
 * fields live in Stripe's iframe and cannot read our CSS, so the tokens are
 * read here at runtime instead of copied as hex values.
 */
export const checkoutAppearance = (): Appearance => {
  const border = `1px solid ${token("input")}`
  return {
    theme: "night",
    variables: {
      colorPrimary: token("primary"),
      colorBackground: token("card"),
      colorText: token("foreground"),
      colorTextSecondary: token("muted-foreground"),
      colorTextPlaceholder: token("muted-foreground"),
      colorDanger: token("destructive"),
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSizeBase: "14px",
      // rounded-md: --radius minus 2px
      borderRadius: "6px",
      spacingUnit: "4px",
    },
    rules: {
      ".Input": {
        border,
        backgroundColor: token("input", 0.3),
        boxShadow: "none",
      },
      ".Input:focus": { border, boxShadow: `0 0 0 1px ${token("ring")}` },
      ".Input--invalid": {
        border: `1px solid ${token("destructive")}`,
        boxShadow: "none",
      },
      ".Label": { color: token("foreground"), fontWeight: "500" },
      ".AccordionItem": {
        border,
        backgroundColor: "transparent",
        boxShadow: "none",
      },
    },
  }
}
